import api from 'api';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {EmailSettings, emailSettingsSchema} from 'types/EmailSettings';
import {TransportType} from 'types/TransportType';
import {AbstractTestTransport} from './common/AbstractTestTransport';
import {ResultLabel} from './common/ResultLabel';
import {Server} from 'types/transport/Abstract/AbstractServer';
import {castToSend} from 'common/yup/castToSend';
import {SendEmailForm} from 'components/forms/SendEmailForm';
import {usePostApi} from 'common/hooks/usePostApi';
import Loader from 'components/Loader';
import {Alert, Box} from '@mui/material';

type ServerProps = Pick<Server, 'address' | 'port'>;

type Props = {
   values: EmailSettings;
};

type Props2 = {
   username: EmailSettings['outgoingServer']['username'];
   password: EmailSettings['outgoingServer']['password'];
   address: EmailSettings['outgoingServer']['address'];
   port: EmailSettings['outgoingServer']['port'];
   authentication: EmailSettings['outgoingServer']['authentication'];
   getValues: () => EmailSettings;
};

const emailSettingsContext = React.createContext<
   (() => {address: string; port: number} | EmailSettings) | null
>(null);

export const TestOutgoingEmailTransport = React.memo((props: Props | Props2) => {
   let {username, password, address, port, authentication, getValues} = props as Props2;

   const {values} = props as Props;
   username = values?.outgoingServer?.username || username;
   password = values?.outgoingServer?.password || password;
   address = values?.outgoingServer?.address || address;
   port = values?.outgoingServer?.port || port;
   authentication = values?.outgoingServer?.authentication || authentication;

   const disabled = !address || !port;

   let url, RenderLabels: React.ElementType<{result: any}>;

   if (!authentication) {
      url = api.serverTest;
      RenderLabels = RenderLabelsFreeServer;
   } else if (!username || !password) {
      url = api.serverTest;
      RenderLabels = RenderLabelsServer;
   } else {
      url = api.testOutboundEmailServer;
      RenderLabels = RenderLabelsEmail;
   }

   const getData = () =>
      !authentication || !username || !password
         ? {
              address: address!,
              port: port!,
           }
         : castToSend(emailSettingsSchema, values || getValues());

   const {Provider} = emailSettingsContext;

   return (
      <Provider value={getData}>
         <AbstractTestTransport
            api={url}
            getData={getData}
            transportType={TransportType.enum.EMAIL}
            disabled={!!disabled}
            RenderLabels={RenderLabels}
            onlyLables
         />
      </Provider>
   );
});

const RenderLabelsFreeServer = ({result}: {result: boolean}) => {
   const {t} = useTranslation();

   return (
      <>
         <ResultLabel>{t('Sending email server/account test result:')}</ResultLabel>
         <ResultLabel>
            {result
               ? t('Connection successful.\nAuthentication not required; login not tested.')
               : t('Connection not successful.')}
         </ResultLabel>
      </>
   );
};

const RenderLabelsServer = ({result}: {result: boolean}) => {
   const {t} = useTranslation();

   return (
      <>
         <ResultLabel>{t('Sending email server/account test result:')}</ResultLabel>
         <ResultLabel>
            {result
               ? t('Connection successful.\nUsername or password missing; login not tested.')
               : t('Connection not successful.')}
         </ResultLabel>
      </>
   );
};

type TestOutEmailResult = [boolean, boolean, boolean];
const RenderLabelsEmail = ({result}: {result: TestOutEmailResult}) => {
   const {t} = useTranslation();

   const getData = React.useContext(emailSettingsContext);
   const {request, state, loading, error} = usePostApi<TestOutEmailResult>(
      api.testOutboundEmailServer
   );

   return (
      <>
         <ResultLabel>{t('Sending email server/account test result:')}</ResultLabel>
         <ResultLabel>
            {!result[0]
               ? t('Connection not successful.')
               : !result[1]
               ? t('Connection successful.\nLogin not successful.')
               : t('Connection successful.\nLogin successful.')}
         </ResultLabel>
         <Box marginTop="2rem" marginBottom="2rem">
            <SendEmailForm
               onSubmit={(val) => {
                  const values = getData?.();
                  console.debug('send email', val, values);
                  request(JSON.stringify(values), {
                     searchParams: {sendTestEmail: 'true', testEmailAddress: val.email},
                  });
               }}
            />
         </Box>
         {loading && <Loader />}
         {state && state[2] && (
            <Alert severity="success">
               {t('Email delivered.  Please check the recipient Inbox')}
            </Alert>
         )}
         {((state && !Boolean(state[2])) || error) && (
            <Alert severity="error">
               {t('Email delivery failed.  Check SMTP configuration.')}
               {error}
            </Alert>
         )}
      </>
   );
};
